import { useCallback, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useReactFlow } from 'reactflow';
import { getElkLayoutedElements } from 'assets/js/pages/engage/workflows/utils/getElkLayoutedElements';
import {
    shouldLayoutSelector,
    useReactFlowStore,
} from 'assets/js/pages/engage/workflows/utils/store';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { debounce } from 'lodash';

const useLayout = () => {
    const { accountId } = useCurrentAccount();
    const history = useHistory();
    const shouldLayout = useReactFlowStore(shouldLayoutSelector);
    const { setShouldLayout, setNodes, setEdges, getNodes, getEdges, lastSelectedNodeId } =
        useReactFlowStore(
            ({ setShouldLayout, setNodes, setEdges, getNodes, getEdges, lastSelectedNodeId }) => ({
                setShouldLayout,
                setNodes,
                setEdges,
                getNodes,
                getEdges,
                lastSelectedNodeId,
            }),
        );
    const { fitView } = useReactFlow();

    useEffect(() => {
        const handleResize = debounce(() => setShouldLayout(true), 500);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [setShouldLayout]);

    const applyLayout = useCallback(async () => {
        const nodes = getNodes();
        const edges = getEdges();

        if (!nodes.length) return;

        try {
            const { layoutedNodes, layoutedEdges } = await getElkLayoutedElements(nodes, edges);

            setNodes([...layoutedNodes], { setEdges: false });
            setEdges([...layoutedEdges]);

            const selectedNode = layoutedNodes.find(n => n.selected);

            const nodeToFocusId = selectedNode?.id || lastSelectedNodeId;

            setTimeout(() => {
                fitView({
                    nodes: nodeToFocusId ? [{ id: nodeToFocusId }] : undefined,
                    maxZoom: 1,
                    minZoom: 1,
                    duration: 250,
                });
            }, 0);
        } catch (e) {
            history.push(`/accounts/${accountId}/workflows`);
        }
    }, [accountId, history, lastSelectedNodeId, setNodes, setEdges, fitView, getNodes, getEdges]);

    useEffect(() => {
        if (shouldLayout) {
            setShouldLayout(false);
            window.requestAnimationFrame(applyLayout);
        }
    }, [shouldLayout, applyLayout, setShouldLayout]);
};

export { useLayout };
